/**
 * Memory — short narrated cutscene that plays after a synchronized tend.
 * Full-bleed painted backdrop + typewriter text + grandmother voice.
 */
import Phaser from 'phaser';
import { Audio } from '../systems/AudioManager.js';
import { MEMORIES } from '../data/plants.js';

export class MemoryScene extends Phaser.Scene {
  constructor() { super({ key: 'MemoryScene' }); }

  init(data) {
    this.memoryId = data?.memoryId || 'wedding_dance';
    this.plantId = data?.plantId;
    this.roomCode = data?.roomCode;
    this.role = data?.role;
  }

  create() {
    const memory = MEMORIES[this.memoryId];
    if (!memory) { this._exit(); return; }

    this._memory = memory;
    this.cameras.main.fadeIn(1200, 255, 244, 160);
    this.cameras.main.setBackgroundColor('#0a0612');

    Audio.attach(this);
    if (memory.music) {
      Audio.playMusic(memory.music, { fadeIn: 2500, fadeOut: 1000, volume: 0.4 });
    }

    this._layout();
    this.scale.on('resize', this._onResize, this);

    // Skip on tap (after any beat has started)
    this.input.on('pointerdown', () => this._skipForward());

    // Start beats
    this._playBeats(memory);
  }

  _onResize() {
    // Keep it simple — redraw static layout; beats keep ticking
    this.children.removeAll(true);
    this._layout();
  }

  _layout() {
    const w = this.scale.width;
    const h = this.scale.height;
    const memory = this._memory;

    // === Painted backdrop, cover mode ===
    if (memory.backdrop && this.textures.exists(memory.backdrop)) {
      const bg = this.add.image(w / 2, h / 2, memory.backdrop).setDepth(0);
      const scale = Math.max(w / bg.width, h / bg.height);
      bg.setScale(scale);
      // Gentle darken for text
      const vig = this.add.graphics().setDepth(1);
      vig.fillStyle(0x0a0612, 0.3);
      vig.fillRect(0, 0, w, h);
    } else {
      const g = this.add.graphics().setDepth(0);
      const c = memory.backdropColor || 0x1a0e2e;
      g.fillGradientStyle(c, c, 0x1a0e2e, 0x0a0612, 0.6, 0.6, 1, 1);
      g.fillRect(0, 0, w, h);
    }

    // Drifting light motes
    for (let i = 0; i < Math.floor(w / 18); i++) {
      const x = Math.random() * w;
      const y = Math.random() * h;
      const d = this.add.circle(x, y, 0.9 + Math.random() * 1.2, 0xfff4a0, 0.5 + Math.random() * 0.4).setDepth(5);
      this.tweens.add({
        targets: d,
        y: y - 60, alpha: { from: d.alpha, to: 0 },
        duration: 6000 + Math.random() * 4000, repeat: -1,
        onRepeat: () => { d.x = Math.random() * this.scale.width; d.y = this.scale.height + 10; d.alpha = 0.5 + Math.random() * 0.4; },
      });
    }

    // === Title at top (after brief delay) ===
    const titleSize = Math.min(30, Math.max(22, w * 0.07));
    const title = this.add.text(w / 2, h * 0.12, memory.title, {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: `${titleSize}px`,
      fontStyle: 'italic',
      color: '#f4ecd8',
      stroke: '#0a0612',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(20).setAlpha(0);
    this.tweens.add({
      targets: title, alpha: 1, y: h * 0.12 - 6,
      duration: 1500, delay: 400, ease: 'Sine.easeOut',
    });

    // === Dialogue panel at bottom ===
    const panelH = Math.max(130, h * 0.22);
    const panel = this.add.graphics().setDepth(15);
    panel.fillStyle(0x0a0612, 0.82);
    panel.fillRect(0, h - panelH, w, panelH);
    panel.lineStyle(1, 0xd4b08a, 0.4);
    panel.beginPath();
    panel.moveTo(0, h - panelH);
    panel.lineTo(w, h - panelH);
    panel.strokePath();

    this._beatText = this.add.text(w / 2, h - panelH / 2, this._currentBeatText || '', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: `${Math.max(18, Math.min(24, w * 0.055))}px`,
      fontStyle: 'italic',
      color: '#f4ecd8',
      align: 'center',
      wordWrap: { width: w - 60 },
    }).setOrigin(0.5).setDepth(16);

    // Skip hint
    this.add.text(w - 14, 14, 'tap to skip', {
      fontFamily: 'Cormorant Garamond, serif',
      fontSize: '12px',
      fontStyle: 'italic',
      color: '#6a5a4a',
    }).setOrigin(1, 0).setDepth(20);
  }

  _playBeats(memory) {
    let cum = 0;
    memory.beats.forEach((beat, i) => {
      cum += beat.delay;
      const evt = this.time.delayedCall(cum, () => {
        this._currentBeatText = beat.text;
        this._typewriter(beat.text);
        if (beat.voice) Audio.playVoice(beat.voice, { volume: 0.95 });
        if (i === memory.beats.length - 1) {
          this.time.delayedCall(6000, () => this._exit());
        }
      });
      if (!this._beatTimers) this._beatTimers = [];
      this._beatTimers.push(evt);
    });
  }

  _typewriter(text) {
    if (this._typeTimer) this._typeTimer.remove();
    if (!this._beatText) return;
    this._beatText.setText('');
    let idx = 0;
    this._typeTimer = this.time.addEvent({
      delay: 35, loop: true,
      callback: () => {
        idx++;
        if (this._beatText) this._beatText.setText(text.substring(0, idx));
        if (idx >= text.length) this._typeTimer.remove();
      },
    });
  }

  _skipForward() {
    if (!this._skipCount) this._skipCount = 0;
    this._skipCount++;
    if (this._skipCount > 2) this._exit();
  }

  _exit() {
    if (this._beatTimers) this._beatTimers.forEach(t => t && t.remove && t.remove());
    if (this._typeTimer) this._typeTimer.remove();
    this.scale.off('resize', this._onResize, this);
    Audio.playMusic('ambient_garden', { fadeIn: 2000, fadeOut: 1500, volume: 0.38 });
    this.cameras.main.fadeOut(1200, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GardenScene', { roomCode: this.roomCode, role: this.role });
    });
  }
}
